import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { LogOut, Users, ChefHat, UtensilsCrossed, ClipboardList, DollarSign, Shield, Settings, Wine, Receipt, QrCode, Palette, Soup, ShoppingCart, FileText, TrendingUp, CalendarCheck, MessageSquare, Contact, CreditCard, Printer } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { getActiveOrders } from '../api/restaurant';
import api from '../api/axios';

// Role-based page access
const ROLE_PAGES = {
  serveur:    ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/orders/new', '/staff/reservations'],
  caissier:   ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/qr-codes', '/staff/orders/new', '/staff/reservations'],
  cuisinier:  ['/kitchen'],
  admin:      ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/qr-codes', '/kitchen', '/staff/payments', '/staff/accounting', '/staff/users', '/staff/vitrine', '/staff/sauces-config', '/staff/orders/new', '/staff/stats', '/staff/reservations', '/staff/reviews', '/staff/clients', '/staff/payment-methods'],
  webmaster:  ['/staff/orders', '/staff/tables', '/staff/drinks', '/staff/menu', '/staff/qr-codes', '/kitchen', '/staff/payments', '/staff/accounting', '/staff/users', '/staff/vitrine', '/staff/config', '/staff/sauces-config', '/staff/orders/new', '/staff/invoice-templates', '/staff/stats', '/staff/reservations', '/staff/reviews', '/staff/clients', '/staff/payment-methods', '/staff/printer'],
};

const NAV_ITEMS = [
  { path: '/staff/orders/new', icon: ShoppingCart, label: 'Prendre Commande', color: 'hover:border-[#8B0000]' },
  { path: '/staff/menu', icon: UtensilsCrossed, label: 'Gestion Menu', color: 'hover:border-[#3d2817]' },
  { path: '/staff/orders', icon: ClipboardList, label: 'Commandes', color: 'hover:border-orange-500', showBadge: true },
  { path: '/staff/tables', icon: Users, label: 'Tables', color: 'hover:border-blue-500' },
  { path: '/kitchen', icon: ChefHat, label: 'Cuisine', color: 'hover:border-green-500' },
  { path: '/staff/drinks', icon: Wine, label: 'Boissons', color: 'hover:border-amber-500' },
  { path: '/staff/payments', icon: Receipt, label: 'Paiements', color: 'hover:border-teal-500' },
  { path: '/staff/qr-codes', icon: QrCode, label: 'QR Codes', color: 'hover:border-indigo-500' },
  { path: '/staff/accounting', icon: DollarSign, label: 'Comptabilite', color: 'hover:border-emerald-500' },
  { path: '/staff/users', icon: Shield, label: 'Utilisateurs', color: 'hover:border-purple-500' },
  { path: '/staff/vitrine', icon: Palette, label: 'Vitrine', color: 'hover:border-pink-500' },
  { path: '/staff/sauces-config', icon: Soup, label: 'Sauces & Accomp.', color: 'hover:border-orange-500' },
  { path: '/staff/payment-methods', icon: CreditCard, label: 'Modes Paiement', color: 'hover:border-teal-500' },
  { path: '/staff/printer', icon: Printer, label: 'Imprimante', color: 'hover:border-violet-500' },
  { path: '/staff/config', icon: Settings, label: 'Configuration', color: 'hover:border-red-500' },
  { path: '/staff/invoice-templates', icon: FileText, label: 'Templates Facture', color: 'hover:border-rose-500' },
  { path: '/staff/stats', icon: TrendingUp, label: 'Tableau de Bord', color: 'hover:border-emerald-500' },
  { path: '/staff/reservations', icon: CalendarCheck, label: 'Reservations', color: 'hover:border-cyan-500', showResBadge: true },
  { path: '/staff/reviews', icon: MessageSquare, label: 'Avis Clients', color: 'hover:border-amber-500' },
  { path: '/staff/clients', icon: Contact, label: 'Fichier Clients', color: 'hover:border-blue-500' },
];

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [orderCount, setOrderCount] = useState(0);
  const [pendingResCount, setPendingResCount] = useState(0);
  const staffRole = localStorage.getItem('staffRole') || 'serveur';
  const allowedPages = ROLE_PAGES[staffRole] || [];

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }

    // Redirect cuisinier directly to kitchen
    if (staffRole === 'cuisinier') { navigate('/kitchen'); return; }

    fetchOrderCount();
    fetchPendingRes();
  }, [navigate, staffRole]);

  const fetchOrderCount = async () => {
    try {
      const data = await getActiveOrders();
      setOrderCount(data.length);
    } catch (error) { console.error('Error:', error); }
  };

  const fetchPendingRes = async () => {
    try {
      const res = await api.get('/reservations/pending-count');
      setPendingResCount(res.data.count);
    } catch (error) { console.error('Error:', error); }
  };

  const handleLogout = () => {
    localStorage.removeItem('staffToken');
    localStorage.removeItem('staffUsername');
    localStorage.removeItem('staffName');
    localStorage.removeItem('staffRole');
    toast({ title: 'Deconnexion reussie', description: 'A bientot !' });
    navigate('/staff/login');
  };

  const visibleItems = NAV_ITEMS.filter(item => allowedPages.includes(item.path));

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src="/logo-taverne.png" alt="La Taverne" className="h-12" />
            <div>
              <p className="text-sm font-semibold">Tableau de Bord</p>
              <p className="text-xs text-gray-300">{localStorage.getItem('staffName')} | {staffRole}</p>
            </div>
          </div>
          <Button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white" data-testid="logout-btn">
            <LogOut className="mr-2" size={18} /> Deconnexion
          </Button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {visibleItems.map(item => {
            const Icon = item.icon;
            return (
              <button key={item.path} data-testid={`nav-${item.path.split('/').pop()}`}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-2 p-4 sm:gap-3 sm:p-5 rounded-xl border-2 bg-white text-gray-700 border-gray-200 ${item.color} hover:shadow-md transition-all cursor-pointer relative`}>
                <Icon size={22} className="sm:w-7 sm:h-7" />
                <span className="font-bold text-xs sm:text-sm">{item.label}</span>
                {item.showBadge && orderCount > 0 && <Badge className="bg-red-500 text-white text-xs absolute top-2 right-2">{orderCount}</Badge>}
                {item.showResBadge && pendingResCount > 0 && <Badge className="bg-orange-500 text-white text-xs absolute top-2 right-2">{pendingResCount}</Badge>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
